import type { Capability, View } from '@clawharbor/manifest';
import type { TenantTab } from './route.js';
import type {
  Billing,
  DraftSummary,
  OnboardJob,
  Overview,
  Ownership,
  ProofChallenge,
  ProofVerification,
  Principal,
  PublishReceipt,
  TwoFactorEnrolment,
  TwoFactorStatus,
  RunResult,
  TenantDetail,
  UsageWindow,
} from './api.js';

/**
 * One mutable state object plus a repaint signal — the same shape the onboarding
 * console and apps/web use, for the same reason: there is exactly one writer,
 * and what needs coalescing is the PAINT, not the state.
 *
 * Everything an operator has typed but not yet sent lives here as a draft next
 * to the server's own copy, never merged into it. A card therefore always knows
 * the difference between "the manifest says this" and "you typed this", which is
 * what lets the 30-character check run before the request instead of after it.
 */

/**
 * The destinations.
 *
 * `sites` and `traffic` are the words a customer with one site uses; `fleet`
 * and `usage` were the words the operator who built it used. `host` is the
 * operator-only destination the dark tenants and the monitor moved to, so the
 * first screen a customer sees stops being an operator console with a filter
 * applied. `connect` is gone as a screen: it rendered one card per tenant from
 * the same list Sites renders, and each card is now that tenant's Connect tab.
 *
 * `overview` is the front page: is anything wrong, and what happened this
 * window, across every site at once. `settings` replaced `billing` as a
 * destination because the plan was never the only thing a person goes looking
 * for under that word — the account's own security and the theme live there
 * too, and `#/billing` still parses to it.
 */
export type Screen = 'login' | 'overview' | 'sites' | 'crawl' | 'tenant' | 'traffic' | 'settings' | 'host';

/**
 * The onboard screen: one form, one job being watched, one list of recent jobs.
 *
 * Kept as a block rather than a dozen top-level fields because the whole of it
 * is dropped together — when the job id in the hash changes, when a person
 * starts another crawl, when the session ends — and a block that is replaced
 * wholesale cannot leave last job's draft under this job's progress bar.
 */
export interface OnboardState {
  /** The address typed into the form. */
  url: string;
  ownership: Ownership;
  /** `POST /api/onboard` is in flight. */
  starting: boolean;
  /** The submit's refusal, verbatim: a private address, robots.txt, the budget. */
  startError: string | null;

  /** The job in the hash. Null is the form. */
  jobId: string | null;
  /** The last record the poll read. Null until the first answer. */
  job: OnboardJob | null;
  /** The poll's failure, kept beside the last good record rather than over it. */
  jobError: string | null;

  /** The draft's summary once the job succeeded; never before. */
  draft: DraftSummary | null;
  draftError: string | null;

  publishing: boolean;
  /** `/publish` said no. The server's sentence and its code, rendered as sent. */
  publishError: { message: string; code: string } | null;
  /** `/publish` said yes, with the URLs it now answers on. */
  published: PublishReceipt | null;

  cancelling: boolean;

  /** `GET /api/onboard` — the last 25 jobs on the host. Null is "not loaded". */
  jobs: OnboardJob[] | null;
  jobsError: string | null;
}

export function initialOnboard(): OnboardState {
  return {
    url: '',
    // The safe default: a crawl asserting nothing about ownership publishes
    // WITH the disclaimer, and the person has to choose to say otherwise.
    ownership: 'third-party',
    starting: false,
    startError: null,
    jobId: null,
    job: null,
    jobError: null,
    draft: null,
    draftError: null,
    publishing: false,
    publishError: null,
    published: null,
    cancelling: false,
    jobs: null,
    jobsError: null,
  };
}

export interface SecurityState {
  /** `GET /api/auth/2fa`. Null is "not loaded". */
  twoFactor: TwoFactorStatus | null;
  enrolment: TwoFactorEnrolment | null;
  recoveryCodes: string[] | null;
  /** The six digits (or a recovery code) typed into whichever form is open. */
  code: string;
  /** Which request is in flight, so only its button says so. */
  busy: 'setup' | 'confirm' | 'disable' | 'recovery' | 'logout-all' | null;
  /** Which form the code field belongs to while 2FA is on. */
  pending: 'disable' | 'recovery' | null;
  error: string | null;
}

export function initialSecurity(): SecurityState {
  return { twoFactor: null, enrolment: null, recoveryCodes: null, code: '', busy: null, pending: null, error: null };
}

/** The three states of the theme control. See ui/theme.ts for what each paints. */
export type ThemeChoice = 'system' | 'light' | 'dark';

export interface ToolDraft {
  title: string;
  description: string;
}

export interface ViewDraft {
  layout: View['layout'];
  title: string;
  linkField: string;
  /** Comma-separated on the way in and out; the array is the wire shape. */
  badges: string;
  slots: Record<string, string>;
}

export interface RunState {
  /** Raw field values as typed. Coerced to the schema's types at send time. */
  args: Record<string, string>;
  running: boolean;
  result: RunResult | null;
  error: string | null;
}

export interface DashboardState {
  screen: Screen;

  /**
   * `GET /api/auth/me` has not answered yet. The frame paints a one-line
   * placeholder rather than the login screen, because a cookie session that
   * flashed "sign in" for 200 ms on every reload would teach people the session
   * does not stick.
   */
  booting: boolean;
  /** Who the server said this page is. Null is the login screen. */
  principal: Principal | null;

  /** The address typed into the login form. Never stored anywhere else. */
  email: string;
  sending: boolean;
  /** A link was requested. The screen says "check your inbox" and no more. */
  sent: boolean;
  /** `CLAWHARBOR_EMAIL_PROVIDER=log` only: the link that would have been emailed. */
  devLink: string | null;
  loginError: string | null;
  /**
   * Why the login screen is showing when it is not the first paint — an expired
   * link, a session that ended. Stated above the form, not as a toast that
   * vanishes before the person reads it.
   */
  loginNotice: string | null;

  /**
   * The operator secret, mirrored here only so the password field stays
   * controlled across repaints. The request copy lives on the api object; both
   * are memory, and neither is ever written to storage or a URL.
   */
  secret: string;
  unlocking: boolean;
  unlockError: string | null;

  /**
   * The account's own security, on the Settings screen.
   *
   * `enrolment` holds a secret the server just minted and has not yet seen a
   * code for; `recoveryCodes` holds codes the server will never send again.
   * Both are memory only and both are dropped the moment the panel moves on,
   * because either one on screen longer than needed is a secret on a monitor.
   */
  security: SecurityState;

  /** `'disabled'` is the 503 `billing_disabled` answer; null is "not loaded". */
  billing: Billing | 'disabled' | null;
  billingBusy: 'checkout' | 'portal' | null;
  billingError: string | null;

  loading: boolean;
  /** A failed load, stated on the screen. Never a blank page. */
  error: string | null;

  overview: Overview | null;
  tenantId: string | null;
  tenant: TenantDetail | null;
  /**
   * Which part of the tenant is on screen. It comes from the hash and never
   * from a click alone, so "show me the Access list for kino-vela" is a link
   * somebody can send rather than a sequence of instructions.
   */
  tenantTab: TenantTab;

  /**
   * What the person chose in the theme control: the system's preference, or
   * one of the two themes outright. `system` is the default and stamps nothing
   * on the root element, so the OS keeps deciding.
   */
  theme: ThemeChoice;

  /** The text typed into the filter above the Sites table. Never in the hash. */
  siteFilter: string;

  usage: UsageWindow | null;
  usageDays: number;
  usageTenantId: string | null;

  /** Tool name → what the operator has typed. Absent means "unedited". */
  drafts: Record<string, ToolDraft>;
  /** Tool name → why the draft cannot be sent yet, checked before the request. */
  draftErrors: Record<string, string>;
  /** Tool name → a PATCH is in flight. */
  savingTool: Record<string, boolean>;
  /** Tool name → the last mutation's failure, shown on the card that caused it. */
  toolErrors: Record<string, string>;

  /** View id → what the operator has typed. */
  viewDrafts: Record<string, ViewDraft>;
  savingView: Record<string, boolean>;
  viewErrors: Record<string, string>;

  /** Tool name → the Run it panel. */
  runs: Record<string, RunState>;

  /** Confirmation key → the text typed so far. Nothing destructive fires without it. */
  confirms: Record<string, string>;

  /**
   * The three proofs for the site on screen, as the challenge response worded
   * them. Held in memory only: the token is in here and the API hands it back
   * from nowhere, so a reload is a fresh challenge — which the panel says.
   */
  proofs: ProofChallenge | null;
  /** The last verify or re-check, printed attempt by attempt under the proofs. */
  proofResult: ProofVerification | null;
  /** A challenge is being issued. */
  issuingProofs: boolean;
  /** A verify or a re-check is in flight. */
  checkingClaim: boolean;
  /** A refusal from either ownership call, verbatim. */
  ownershipError: string | null;

  /**
   * The Connect tab's proxy hosts, as typed: comma or space separated. Null is
   * "unedited", which renders the server's list. Kept as text rather than an
   * array so a half-typed hostname is not parsed, split or dropped mid-word.
   */
  proxyDraft: string | null;
  savingProxy: boolean;
  proxyError: string | null;

  /** The site Settings tab's widget stylesheet, as typed. Null is "unedited". */
  cssDraft: string | null;
  savingCss: boolean;
  cssError: string | null;

  /** A health probe against the live origin is running. */
  checking: boolean;
  /** An unpublish is in flight. */
  unpublishing: boolean;
  dangerError: string | null;

  /** The last thing that happened, stated rather than animated. */
  notice: string | null;
  /** Which copy button last succeeded, so only that one says so. */
  copied: string | null;

  /** The onboard screen, as one block. See `OnboardState`. */
  onboard: OnboardState;
}

export function initialState(): DashboardState {
  return {
    screen: 'login',
    booting: false,
    principal: null,
    email: '',
    sending: false,
    sent: false,
    devLink: null,
    loginError: null,
    loginNotice: null,
    secret: '',
    unlocking: false,
    unlockError: null,
    security: initialSecurity(),
    billing: null,
    billingBusy: null,
    billingError: null,
    loading: false,
    error: null,
    overview: null,
    tenantId: null,
    tenant: null,
    tenantTab: 'overview',
    theme: 'system',
    siteFilter: '',
    usage: null,
    usageDays: 30,
    usageTenantId: null,
    drafts: {},
    draftErrors: {},
    savingTool: {},
    toolErrors: {},
    viewDrafts: {},
    savingView: {},
    viewErrors: {},
    runs: {},
    confirms: {},
    proofs: null,
    proofResult: null,
    issuingProofs: false,
    checkingClaim: false,
    ownershipError: null,
    proxyDraft: null,
    savingProxy: false,
    proxyError: null,
    cssDraft: null,
    savingCss: false,
    cssError: null,
    checking: false,
    unpublishing: false,
    dangerError: null,
    notice: null,
    copied: null,
    onboard: initialOnboard(),
  };
}

/** The manifest's own minimum, enforced here so the mistake costs no round trip. */
export const DESCRIPTION_MIN = 30;

export function draftOf(state: DashboardState, cap: Capability): ToolDraft {
  return state.drafts[cap.name] ?? { title: cap.title, description: cap.description };
}

export function isDirty(state: DashboardState, cap: Capability): boolean {
  const d = state.drafts[cap.name];
  if (!d) return false;
  return d.title !== cap.title || d.description !== cap.description;
}

export function viewDraftOf(state: DashboardState, view: View): ViewDraft {
  return (
    state.viewDrafts[view.id] ?? {
      layout: view.layout,
      title: view.title ?? '',
      linkField: view.linkField ?? '',
      badges: (view.badges ?? []).join(', '),
      slots: { ...(view.slots ?? {}) },
    }
  );
}

export function runOf(state: DashboardState, tool: string): RunState {
  return state.runs[tool] ?? { args: {}, running: false, result: null, error: null };
}

type Listener = () => void;

export class DashboardStore {
  state: DashboardState = initialState();
  private listeners = new Set<Listener>();
  private queued = false;

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  notify(): void {
    if (this.queued) return;
    this.queued = true;
    requestAnimationFrame(() => {
      this.queued = false;
      for (const fn of this.listeners) fn();
    });
  }

  patch(next: Partial<DashboardState>): void {
    Object.assign(this.state, next);
    this.notify();
  }

  /** Replaces one entry of a keyed map without mutating the map in place. */
  patchMap<K extends 'drafts' | 'draftErrors' | 'savingTool' | 'toolErrors' | 'viewDrafts' | 'savingView' | 'viewErrors' | 'runs' | 'confirms'>(
    key: K,
    id: string,
    value: DashboardState[K][string] | null,
  ): void {
    const next = { ...this.state[key] } as DashboardState[K];
    if (value === null) delete next[id];
    else next[id] = value;
    this.patch({ [key]: next } as Partial<DashboardState>);
  }

  /** Replaces fields of the onboard block without mutating it in place. */
  patchOnboard(next: Partial<OnboardState>): void {
    this.patch({ onboard: { ...this.state.onboard, ...next } });
  }

  /**
   * Drop every draft, error and run panel.
   *
   * Called when the tenant changes. Carrying a half-typed description across
   * tenants would put one tenant's text into another's PATCH, and carrying a run
   * result would show rows from a tool that is not on the screen.
   */
  clearTenantWork(): void {
    this.patch({
      drafts: {},
      draftErrors: {},
      savingTool: {},
      toolErrors: {},
      viewDrafts: {},
      savingView: {},
      viewErrors: {},
      runs: {},
      confirms: {},
      dangerError: null,
      // The proofs carry a token issued for ONE tenant. Carrying them to the
      // next site would print another domain's challenge under this one's name.
      proofs: null,
      proofResult: null,
      issuingProofs: false,
      checkingClaim: false,
      ownershipError: null,
      // Another site's hostnames or stylesheet must never ride into this one's PATCH.
      proxyDraft: null,
      savingProxy: false,
      proxyError: null,
      cssDraft: null,
      savingCss: false,
      cssError: null,
    });
  }
}

/**
 * What the screens are allowed to do.
 *
 * Declared here rather than in the view layer so a screen can import the type
 * without importing the frame that renders it.
 */
export interface DashboardActions {
  setEmail(value: string): void;
  /** Asks the server to email a sign-in link. Says "sent" for any well-formed address. */
  sendLink(): void;
  /** Back from "check your inbox" to the form, to try another address. */
  resetLogin(): void;
  /** POST /api/auth/logout — clears the cookie session. Accounts only. */
  signOut(): void;

  setSecret(value: string): void;
  unlock(): void;
  /** Forgets the operator secret held in memory. The account path signs out instead. */
  forget(): void;

  /** POST /api/auth/logout-all — every other session of this account ends. */
  signOutElsewhere(): void;
  /** Mint an unconfirmed secret and show it. */
  startTwoFactor(): void;
  /** Abandon an enrolment, or close the disable / regenerate form. */
  cancelTwoFactor(): void;
  setSecurityCode(value: string): void;
  /** Send the typed code to confirm the enrolment; show the recovery codes. */
  confirmTwoFactor(): void;
  /** Open the code form for turning 2FA off or for new recovery codes. */
  askSecurityCode(purpose: 'disable' | 'recovery'): void;
  /** Send the typed code for whichever of the two forms is open. */
  submitSecurityCode(): void;
  /** "I have saved them": the recovery codes leave the screen for good. */
  dismissRecoveryCodes(): void;

  /** POST /api/billing/checkout, then leave for Stripe. */
  checkout(plan: string): void;
  /** POST /api/billing/portal, then leave for Stripe. */
  openPortal(): void;

  go(screen: Screen): void;
  /** Opens a tenant at one of its tabs; Overview when none is named. */
  openTenant(tenantId: string, tab?: TenantTab): void;
  /** Moves between the tabs of the tenant already open. */
  goTenantTab(tab: TenantTab): void;
  reload(): void;

  /** Stamps the choice on the root element and remembers it for the next visit. */
  setTheme(choice: ThemeChoice): void;
  /** Narrows the Sites table as you type. Client-side; the list is already here. */
  setSiteFilter(value: string): void;

  setUsageDays(days: number): void;
  setUsageTenant(tenantId: string | null): void;

  editTool(tool: string, patch: Partial<ToolDraft>): void;
  saveTool(tool: string): void;
  resetTool(tool: string): void;
  setSurface(tool: string, surface: 'web' | 'app', on: boolean): void;
  setVisibility(tool: string, target: 'model' | 'app', on: boolean): void;
  setTtl(tool: string, ttlSec: number): void;
  /** Turn the form relay on or off for one stage tool. The server decides whether it may. */
  setRelay(tool: string, on: boolean): void;

  setRunArg(tool: string, name: string, value: string): void;
  runTool(tool: string): void;
  clearRun(tool: string): void;

  setConfirm(key: string, value: string): void;
  deleteTool(tool: string): void;

  editView(viewId: string, patch: Partial<ViewDraft>): void;
  editSlot(viewId: string, slot: string, value: string): void;
  saveView(viewId: string): void;

  checkHealth(): void;
  unpublish(): void;

  /** The proxy-host field on the Connect tab. */
  editProxyHosts(value: string): void;
  /** PATCH the whole list; an empty field clears it. */
  saveProxyHosts(): void;

  /** The widget stylesheet on the site Settings tab. */
  editCss(value: string): void;
  saveCss(): void;
  /** Back to what the server has. */
  resetCss(): void;

  /**
   * POST /api/ownership/:tenant/challenge — the three proofs, token and all,
   * for the site on screen. The token is returned exactly once.
   */
  issueProofs(): void;
  /**
   * The one button that answers "is it true": a re-check when a claim is already
   * on record, a verify against the open challenge when one is not. The screen
   * does not make a person choose between two words for the same act.
   */
  checkClaim(): void;

  /* ── Onboard ─────────────────────────────────────────────────────────── */

  setOnboardUrl(value: string): void;
  setOnboardOwnership(value: Ownership): void;
  /** POST /api/onboard with the session, then watch the job it names. */
  startOnboard(): void;
  /** Lands on `#/crawl/<jobId>` and polls it: the "resume" affordance. */
  openJob(jobId: string): void;
  /** POST /api/onboard/:id/publish for the job on the screen. */
  publishDraft(): void;
  /** POST /api/onboard/:id/cancel while the crawl is queued or running. */
  cancelCrawl(): void;
  /** Back to the empty form at `#/crawl`, dropping the job on the screen. */
  newCrawl(): void;

  copy(id: string, text: string): void;
  dismissNotice(): void;
}
